import { useState, useEffect, useRef, useCallback } from 'react'
import { Chart, registerables } from 'chart.js'
import { api } from '../api'
import { token } from '../tokens'
import { enumLabel, useI18n } from '../i18n'
import { ErrorState, errorMessage } from './ErrorState'
import type { ActionDistribution } from '../types'
import { ACTION_COLORS } from '../types'

Chart.register(...registerables)

interface Props {
  hours: number
}

export function ActionsChart({ hours }: Props) {
  const { t, fmt } = useI18n()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)
  const [data, setData] = useState<ActionDistribution[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await api.getActionDistribution(hours))
      setError(null)
    } catch (err: unknown) {
      setError(errorMessage(err))
      setData([])
    }
  }, [hours])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!canvasRef.current || data.length === 0) return

    chartRef.current?.destroy()

    // Canvas ne resout pas les variables CSS : on lit la valeur des tokens.
    const colors = data.map((d) =>
      token(ACTION_COLORS[d.action as keyof typeof ACTION_COLORS] ?? '--text-hint'),
    )

    chartRef.current = new Chart(canvasRef.current, {
      type: 'doughnut',
      data: {
        labels: data.map((d) => enumLabel(t, 'action', d.action, d.action)),
        datasets: [
          {
            data: data.map((d) => d.count),
            backgroundColor: colors,
            borderWidth: 2,
            borderColor: token('--bg-card'),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: token('--text'),
            padding: 10,
            cornerRadius: 6,
            bodyFont: { size: 12 },
          },
        },
        cutout: '65%',
        animation: { duration: 300 },
      },
    })

    return () => {
      chartRef.current?.destroy()
    }
  }, [data, t])

  if (error) {
    return (
      <div className="chart-card">
        <div className="chart-title">{t('chart.actionBreakdown')}</div>
        <ErrorState message={error} onRetry={load} compact />
      </div>
    )
  }

  return (
    <div className="chart-card">
      <div className="chart-title">{t('chart.actionBreakdown')}</div>
      <div className="chart-legend">
        {data.map((d) => (
          <span key={d.action} className="legend-item">
            <span
              className="legend-dot"
              style={{ background: ACTION_COLORS[d.action as keyof typeof ACTION_COLORS] ?? '#888' }}
            />
            {enumLabel(t, 'action', d.action, d.action)} {t('chart.percent', { v: fmt.decimal(d.percentage, 0) })}
          </span>
        ))}
      </div>
      <div className="chart-container">
        <canvas ref={canvasRef} />
      </div>
    </div>
  )
}
